import React, { useCallback } from 'react'
import {
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Overflow,
  Space,
} from '@gousto-internal/citrus-react'
import type { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'

export type CmsCarouselProps = {
  carouselAreaLabel: string
  children: React.ReactNode
  options?: EmblaOptionsType
}

export const CmsCarousel = ({ carouselAreaLabel, children, options }: CmsCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  return (
    <Box
      className="embla"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
    >
      <Box className="embla__viewport" ref={emblaRef}>
        <Box
          aria-atomic="true"
          aria-label={carouselAreaLabel}
          aria-live="polite"
          aria-roledescription="carousel"
          className="embla__container"
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, FlexDirection.Row]}
          role="region"
          tabIndex={0}
        >
          {React.Children.map(children, (child, index) => (
            <Box
              aria-label={`${index + 1} of ${React.Children.count(children)}`}
              aria-roledescription={`Slide ${index + 1} of ${React.Children.count(children)}`}
              className="embla__slide"
              data-testid="carousel-slide"
              display={Display.Flex}
              flexGrow={0}
              flexShrink={0}
              justifyContent={JustifyContent.Center}
              key={index}
              paddingH={4}
              paddingV={4}
              role="group"
              tabIndex={-1}
            >
              {child}
            </Box>
          ))}
        </Box>

        <Box
          aria-label="Carousel controls"
          display={Display.Flex}
          position={DisplayPosition.Absolute}
          role="group"
          top="45%"
          width="100%"
        >
          <Space direction="horizontal" size={4} />
          <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween} width="100%">
            <Button
              aria-label="Previous slide"
              className="embla__prev"
              colorVariant={ButtonColorVariant.Secondary}
              icon="arrow_back"
              onClick={scrollPrev}
              size={ButtonSize.Medium}
              style={{ borderRadius: '50%' }}
              variant={ButtonVariant.Icon}
            />
            <Button
              aria-label="Next slide"
              className="embla__next"
              colorVariant={ButtonColorVariant.Secondary}
              icon="arrow_right"
              onClick={scrollNext}
              size={ButtonSize.Medium}
              style={{ borderRadius: '50%' }}
              variant={ButtonVariant.Icon}
            />
          </Box>
          <Space direction="horizontal" size={4} />
        </Box>
      </Box>
    </Box>
  )
}
